import { useState, useEffect, useCallback } from 'react';
import { LuSearch, LuRefreshCw } from 'react-icons/lu';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import ApplicationDrawer from '../../components/ApplicationDrawer/ApplicationDrawer';
import TableCard from '../../components/TableCard/TableCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import { fetchPartners, updatePartner } from '../../lib/directus';
import styles from './Partners.module.css';

const STATUS_MAP = { pending: 'Pending review', approved: 'Approved', declined: 'Declined' };

function mapPartner(p) {
  const contact = p.contact;
  const contactName = contact
    ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') || '—'
    : '—';
  return {
    id: p.id,
    company: p.company_name || '—',
    type: p.account_type ? p.account_type.charAt(0).toUpperCase() + p.account_type.slice(1) : '—',
    contactName,
    contactEmail: contact?.email || '—',
    applied: p.date_created ? new Date(p.date_created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—',
    status: STATUS_MAP[p.status] || p.status || '—',
    address: p.address || '',
    location: p.location || '',
    registrationNumber: p.registration_number || '',
  };
}

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All types');
  const [selected, setSelected] = useState([]);
  const [activePartner, setActivePartner] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchPartners()
      .then(data => setPartners(data.map(mapPartner)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = partners.filter((row) => {
    const matchSearch = row.company.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'All types' || row.type === typeFilter;
    return matchSearch && matchType;
  });

  const toggleRow = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelected(selected.length === filtered.length ? [] : filtered.map((r) => r.id));

  const toolbar = (
    <>
      <div className={styles.toolbarLeft}>
        <div className={styles.searchWrapper}>
          <LuSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search partners...."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className={styles.filterSelect}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option>All types</option>
          <option>Seller</option>
          <option>Buyer</option>
        </select>
      </div>

      <button className={styles.refreshBtn} onClick={() => { setSearch(''); setTypeFilter('All types'); load(); }}>
        <LuRefreshCw size={13} />
        Refresh
      </button>
    </>
  );

  const pagination = (
    <>
      <span className={styles.pageInfo}>Total {filtered.length} items</span>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button className={`${styles.pageBtn} ${styles.pageBtnActive}`}>1</button>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </>
  );

  return (
    <AdminLayout title="Partner applications">
      <ApplicationDrawer
        partner={activePartner}
        onClose={() => setActivePartner(null)}
        onApprove={(p) => updatePartner(p.id, { status: 'approved' }).then(() => { setActivePartner(null); load(); }).catch(console.error)}
        onReject={(p) => updatePartner(p.id, { status: 'declined' }).then(() => { setActivePartner(null); load(); }).catch(console.error)}
      />
      <TableCard toolbar={toolbar} pagination={pagination}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCol}>
                <input
                  type="checkbox"
                  checked={selected.length === filtered.length && filtered.length > 0}
                  onChange={toggleAll}
                />
              </th>
              <th>Company</th>
              <th>Type</th>
              <th>Contact</th>
              <th>Applied</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className={styles.loadingCell}>Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className={styles.loadingCell}>No partners found.</td></tr>
            )}
            {!loading && filtered.map((row) => (
              <tr key={row.id} onClick={() => setActivePartner(row)} style={{ cursor: 'pointer' }}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.includes(row.id)}
                    onChange={() => toggleRow(row.id)}
                  />
                </td>
                <td>{row.company}</td>
                <td>{row.type}</td>
                <td>
                  <div className={styles.contactName}>{row.contactName}</div>
                  <div className={styles.contactEmail}>{row.contactEmail}</div>
                </td>
                <td>{row.applied}</td>
                <td><StatusBadge status={row.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </AdminLayout>
  );
}
